import React, { useEffect, useState } from "react";
import {
  getCredentials,
  rotateLiveKey,
  rotateSandboxKey,
} from "../../services/settings";
import toast from "react-hot-toast";
import { useAuth } from "../../context/AuthContextProvider";
import Toggle from "../../components/Toggle";
import { Password } from "primereact/password";
import { Button } from "primereact/button";

const copyToClipboard = (textToCopy) => {
  navigator.clipboard
    .writeText(textToCopy)
    .then(() => {
      toast.success("Copied to clipboard!");
    })
    .catch((err) => {
      toast.error("Unable to copy to clipboard. Please try again.");
      console.error("Copy to clipboard failed:", err);
    });
};

const ApiKeys = () => {
  const [encryptionIV, setEncryptionIV] = useState("");
  const [encryptionKey, setEncryptionKey] = useState("");
  const [merchantId, setMerchantId] = useState("");
  const [sandboxKey, setSandboxKey] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const { globalProfile } = useAuth();
  const [isRotating, setIsRotating] = useState(false);

  const keys = globalProfile === "TEST" ? sandboxKey : secretKey;

  const handleCopyToClipboard = (textToCopy) => {
    copyToClipboard(textToCopy);
  };

  const handleCredentials = async () => {
    try {
      const res = await getCredentials();
      setEncryptionIV(res.data?.encryptionIV || "");
      setEncryptionKey(res.data?.encryptionKey || "");
      setMerchantId(res.data?.merchantId || "");
      setSecretKey(res.data?.secretKey || "");
      setSandboxKey(res.data?.sandboxKey || "");
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message);
        setEncryptionIV("");
        setEncryptionKey("");
        setMerchantId("");
        setSecretKey("");
        setSandboxKey("");
      }
    }
  };

  const handleRotateKey = async () => {
    setIsRotating(true);
    try {
      if (globalProfile === "TEST") {
        const res = await rotateSandboxKey();
        setSandboxKey(res.data.newKey);
        toast.success("Sandbox key rotated successfully");
      } else {
        const res = await rotateLiveKey();
        setSecretKey(res.data.liveKey);
        toast.success("Live key rotated successfully");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to rotate key");
    } finally {
      setIsRotating(false);
    }
  };

  useEffect(() => {
    handleCredentials();
  }, [globalProfile]);

  return (
    <div>
      <div className="md:flex items-center justify-between">
        <h1 className="text-2xl font-bold text-navy-blue">
          Settings - API Keys
        </h1>
        <Toggle />
      </div>

      <div className="min-h-[924px] max-w-80 md:max-w-full bg-white mt-8 rounded-[10px] p-[3rem]">
        <div className="border border-gray-200 rounded-t-[10px] p-[1rem] pl-7 flex justify-between items-center">
          <p className="text-primary text-xl font-semibold">API Keys</p>
          <span>
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4.5 6.75L9 11.25L13.5 6.75"
                stroke="#464255"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </div>



        <div className="lg:flex items-center gap-6 mt-10 lg:ml-10">
          <div className="flex flex-col">
            <label className="text-navy-blue font-semibold">Merchant ID</label>
            <div className="relative mt-2">
              <Password
                value={merchantId}
                toggleMask
                feedback={false}
                readOnly
                className="md:w-[348px] w-full"
                inputClassName="!bg-form-background md:w-[348px] w-full h-[50px] px-2 pr-10 rounded-md !border-0 focus:outline-none"
              />
            </div>
          </div>
          <div className="mt-8">
            <Button
              label="Copy"
              className="w-[142px] h-[42px] rounded-lg !border !border-primary !text-primary !bg-transparent hover:!bg-primary/5 p-0 justify-center font-normal"
              onClick={() => handleCopyToClipboard(merchantId)}
            />
          </div>
        </div>

        <div className="lg:flex items-center gap-6 mt-10 lg:ml-10">
          <div className="flex flex-col">
            <label className="text-navy-blue font-semibold">Sandbox Key</label>
            <div className="relative mt-2">
              <Password
                value={sandboxKey}
                toggleMask
                feedback={false}
                readOnly
                className="md:w-[348px] w-full"
                inputClassName="!bg-form-background md:w-[348px] w-full h-[50px] px-2 pr-10 rounded-md !border-0 focus:outline-none"
              />
            </div>
          </div>
          <div className="mt-8">
            <Button
              label="Copy"
              className="w-[142px] h-[42px] rounded-lg !border !border-primary !text-primary !bg-transparent hover:!bg-primary/5 p-0 justify-center font-normal"
              onClick={() => handleCopyToClipboard(sandboxKey)}
            />
          </div>
        </div>

        <div className="lg:flex items-center gap-6 mt-10 lg:ml-10">
          <div className="flex flex-col">
            <label className="text-navy-blue font-semibold">Secret Key</label>
            <div className="relative mt-2">
              <Password
                value={keys}
                toggleMask
                feedback={false}
                readOnly
                className="md:w-[348px] w-full"
                inputClassName="!bg-form-background md:w-[348px] w-full h-[50px] px-2 pr-10 rounded-md !border-0 focus:outline-none"
              />
            </div>
          </div>
          <div className="mt-8 flex gap-4">
            <Button
              label="Copy"
              className="w-[142px] h-[42px] rounded-lg !border !border-primary !text-primary !bg-transparent hover:!bg-primary/5 p-0 justify-center font-normal"
              onClick={() => handleCopyToClipboard(keys)}
            />
            <Button
              label={isRotating ? "Rotating..." : "Rotate Key"}
              loading={isRotating}
              className="w-[142px] h-[42px] rounded-lg !bg-primary !border-0 !text-white disabled:opacity-50 p-0 justify-center font-normal"
              onClick={handleRotateKey}
              disabled={isRotating}
            />
          </div>
        </div>
        {Boolean(encryptionIV && encryptionIV.trim()) && (
            <div className="lg:flex items-center gap-6 mt-10 lg:ml-10">
              <div className="flex flex-col">
                <label className="text-navy-blue font-semibold">
                  Encryption IV
                </label>
                <div className="relative mt-2">
                  <Password
                      value={encryptionIV}
                      toggleMask
                      feedback={false}
                      readOnly
                      className="md:w-[348px] w-full"
                      inputClassName="!bg-form-background md:w-[348px] w-full h-[50px] px-2 pr-10 rounded-md !border-0 focus:outline-none"
                  />
                </div>
              </div>
              <div className="mt-8">
                <Button
                    label="Copy"
                    className="w-[142px] h-[42px] rounded-lg !border !border-primary !text-primary !bg-transparent hover:!bg-primary/5 p-0 justify-center font-normal"
                    onClick={() => handleCopyToClipboard(encryptionIV)}
                />
              </div>
            </div>
        )}

        {Boolean(encryptionKey && encryptionKey.trim()) && (
            <div className="lg:flex items-center gap-6 mt-10 lg:ml-10">
              <div className="flex flex-col">
                <label className="text-navy-blue font-semibold">
                  Encryption Key
                </label>
                <div className="relative mt-2">
                  <Password
                      value={encryptionKey}
                      toggleMask
                      feedback={false}
                      readOnly
                      className="md:w-[348px] w-full"
                      inputClassName="!bg-form-background md:w-[348px] w-full h-[50px] px-2 pr-10 rounded-md !border-0 focus:outline-none"
                  />
                </div>
              </div>
              <div className="mt-8">
                <Button
                    label="Copy"
                    className="w-[142px] h-[42px] rounded-lg !border !border-primary !text-primary !bg-transparent hover:!bg-primary/5 p-0 justify-center font-normal"
                    onClick={() => handleCopyToClipboard(encryptionKey)}
                />
              </div>
            </div>
        )}
      </div>
    </div>
  );
};

export default ApiKeys;
